import {
  Alert,
  Button,
  Checkbox,
  CondensedButton,
  Flex,
  Heading,
  IconArrowOpenEndLine,
  IconArrowOpenStartLine,
  IconButton,
  IconCheckDarkSolid,
  IconCheckMarkSolid,
  IconDownloadLine,
  IconGradebookLine,
  IconGroupSolid,
  IconHourGlassLine,
  IconUploadLine,
  IconUserSolid,
  ProgressBar,
  RadioInput,
  RadioInputGroup,
  ScreenReaderContent,
  Spinner,
  Tabs,
  Text,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { auth } from "../../../App";
import { ExternalService } from "../../../services/ExternalService";
import { FileType } from "../../../services/FilesService";
import downloadFile from "../../../utils/Dom";
import { sleep } from "../../../utils/Timer";
import { TruncateToolTip } from "../../../utils/TruncateTooltip";
import { Actions } from "../../Action/Actions";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import DocumentViewer from "../../DocViewer/DocumentViewer";
import { sortOutcomes } from "../../LearningOutcomes/utils";
import { ContextReflections } from "../../Reflection/ContextReflections";
import { SINGLE, Selector } from "../../Selectors/Selector";
import Findings from "../APDashboard/Results/Displays/Findings";
import Recommendations from "../APDashboard/Results/Displays/Recommendations";
import { evidenceFileTypes } from "../Plan/utils";
import ConfirmedModal from "./ConfirmedModal";
import RaterGrid from "./RaterGrid";
import RaterSkeleton from "./RaterSkeleton";
import RosterManagementTray from "./RosterManagementTray";
import UploadFileModal from "./UploadFileModal";
import { sortByLastName } from "./utils";

const Rater = () => {
  const location = useLocation();
  const showNotification = useNotification();
  const [assessingTerm, setAssessingTerm] = useState("");
  const [user, setUser] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [assessorComplete, setAssessorComplete] = useState(false);
  const [assessorsComplete, setAssessorsComplete] = useState(0);
  const [assessorsTotal, setAssessorsTotal] = useState(0);
  const [toggleFindings, setToggleFindings] = useState(
    !(
      location.state.assessment_type &&
      location.state.assessment_type === "external"
    )
  );
  const [scoreView, setScoreView] = useState("myScore");
  const [masteryAt, setMasteryAt] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentArr, setStudentArr] = useState([]);
  const [studentsAssessed, setStudentsAssessed] = useState(0);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [isGradingComplete, setIsGradingComplete] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [section, setSection] = useState(null);
  const [scoreAverage, setScoreAverage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [guide, setGuide] = useState(null);
  const [raterData, setRaterData] = useState(
    JSON.parse(
      window.localStorage.getItem(`raterData/${location.state.share_id}`)
    ) || {}
  );
  const [submittingScores, setSubmittingScores] = useState(false);
  const [hideEvidenceViewer, setHideEvidenceViewer] = useState(false);
  const [fetchEvidence, setFetchEvidence] = useState(false);

  const isJA = location?.state?.assessment_type !== "external";

  const isExternal = location?.state?.assessment_type === "external";

  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);

  const inputs = [
    { value: "myScore", label: t("juriedAssessment.myScore") },
    { value: "aggregate", label: t("juriedAssessment.aggregateScore") },
    { value: "compare", label: t("juriedAssessment.compare") },
  ];

  const fetchAssessmentGuide = () => {
    const lastModifiedFilter = {
      limit: 1,
      sortBy: "last_modified",
      sortOrder: "desc",
    };
    ExternalService.reviewFiles(
      FileType.ASSESSMENT_GUIDES,
      location.state.assessment_id,
      lastModifiedFilter
    ).then((files) => {
      if (files?.length) {
        setGuide(files[0]);
      }
    });
  };

  const fetchInsightTerm = async (insightsTermId) => {
    await ExternalService.getInsightsTerms().then((terms) => {
      const term = terms.find((t) => t.id === insightsTermId);
      setAssessingTerm(term);
    });
  };

  const fetchData = () => {
    setLoading(true);
    fetchAssessmentGuide();
    ExternalService.getAssessmentForPurl(
      location.state.assessment_id,
      location.state.term_id
    ).then(async (assessment) => {
      setAssessment(assessment);
      const masteryMap =
        assessment.assignment_rubric.rating_scales[0]
          .rating_to_mastery_level_map;
      const collecting_terms = assessment.terms.map((term) => term.ddl_id);
      if (isJA) {
        // External Juried Assessment
        if (assessment.assessment_type === "external") {
          const evidencePromise = ExternalService.downloadFiles(
            "evidence",
            assessment.id,
            true
          );
          const purlDataPromise = ExternalService.getPurlDataById(
            location.state.purl_id
          );

          const [files, data] = await Promise.all([
            evidencePromise,
            purlDataPromise,
          ]);

          const studentsEvidenceArr = [];
          files.forEach((file, i) => {
            studentsEvidenceArr.push({
              id: file.file_name,
              index: i + 1,
              attachments: [{ url: file.file_url, name: file.file_name }],
              canvas_id: -1,
              label: `${t("juriedAssessment.student")} ${i + 1}`,
            });
          });
          setStudentArr(studentsEvidenceArr);

          const assessTerm = data.terms.find(
            (t) => t.ddl_id === location.state.term_id
          );
          await fetchInsightTerm(assessTerm.insights_term_id);
          if (studentsEvidenceArr.length > 0) {
            setSelectedStudent(studentsEvidenceArr[0]);
            buildGrid(
              studentsEvidenceArr,
              masteryMap,
              data,
              assessTerm,
              assessment
            );
          } else {
            setSelectedStudent(null);
            setLoading(false);
            setOpenConfirmModal(false);
          }
        } else {
          // Canvas Juried Assessment
          const evidencePromise = ExternalService.getAssessmentStudentsEvidence(
            location.state.assessment_id,
            collecting_terms
          );
          const purlDataPromise = ExternalService.getPurlDataById(
            location.state.purl_id
          );

          const [studentsEvidenceMap, data] = await Promise.all([
            evidencePromise,
            purlDataPromise,
          ]);

          const studentsEvidenceArr = [];
          studentsEvidenceMap.forEach((evidence, i) => {
            studentsEvidenceArr.push({
              id: "" + evidence.user_id,
              index: i + 1,
              attachments: evidence.attachments,
              ddl_evidence_id: evidence.ddl_evidence_id,
              label: `${t("juriedAssessment.student")} ${i + 1}`,
            });
          });
          setStudentArr(studentsEvidenceArr);

          const assessTerm = data.terms.find(
            (t) => t.ddl_id === location.state.term_id
          );
          await fetchInsightTerm(assessTerm.insights_term_id);
          if (studentsEvidenceArr.length > 0) {
            setSelectedStudent(studentsEvidenceArr[0]);
            buildGrid(
              studentsEvidenceArr,
              masteryMap,
              data,
              assessTerm,
              assessment
            );
          } else {
            setSelectedStudent(null);
            setLoading(false);
            setOpenConfirmModal(false);
          }
        }
      } else {
        // External Assessment
        ExternalService.getPurlDataById(location.state.purl_id).then(
          async (data) => {
            const assessTerm = data.terms.find(
              (t) => t.ddl_id === location.state.term_id
            );
            await fetchInsightTerm(assessTerm.insights_term_id);
            data.section.name = data.section.name.replace(/,/g, ", ");
            setSection(data.section);
            ExternalService.getExternalRosterByContext(
              data.section.ddl_id
            ).then((roster) => {
              roster = roster.sort(sortByLastName);
              setRoster(roster);
              const studentsEvidenceArr = [];
              roster.forEach((student) => {
                studentsEvidenceArr.push({
                  attachments: "",
                  ddl_evidence_id: "",
                  id: "" + student.ddl_id,
                  label: student.name ? student.name : student.canvas_id,
                  canvas_id: student.canvas_id,
                });
              });
              setStudentArr(studentsEvidenceArr);
              if (roster.length > 0) {
                setSelectedStudent(studentsEvidenceArr[0]);
                buildGrid(
                  studentsEvidenceArr,
                  masteryMap,
                  data,
                  assessTerm,
                  assessment
                );
              } else {
                setSelectedStudent(null);
                setLoading(false);
                setOpenConfirmModal(false);
              }
            });
          }
        );
      }
    });
  };

  /**
   * Fetches data for the first time
   */
  useEffect(() => {
    if (user && location?.state?.assessment_id && openConfirmModal) {
      fetchData();
    }
  }, [user, location.state.assessment_id, openConfirmModal]);

  const calculateColumnSpan = (level, ratingToMasteryLevelMap) => {
    let count = 0;
    ratingToMasteryLevelMap.levels.forEach((lvl) => {
      if (lvl.mastery_level_id === level.mastery_level_id) {
        count++;
      }
    });
    return count;
  };

  const buildGrid = async (
    studentsEvidenceArr,
    masteryMap,
    data,
    assessTerm,
    assessment
  ) => {
    const colsObj = [];
    const rowObj = [{ id: "raterRow" }];
    // Instantiating out the rows
    data.outcomes = sortOutcomes(data.outcomes);
    data.outcomes.forEach((outcome) => {
      const obj = {
        slo: `${outcome.name}: ${outcome.statement}`,
        id: outcome.id,
        canvas_id: outcome.canvas_id,
        na: false,
        hideSelected: false,
        hideAggregate: true,
      };
      rowObj.push(obj);
    });
    const ratingScalePromise =
      ExternalService.getRatingToMasteryLevelMapsById(masteryMap);
    const masteryLevelsPromise = ExternalService.getMasteryLevels();

    const [map, levels] = await Promise.all([
      ratingScalePromise,
      masteryLevelsPromise,
    ]);

    setMasteryAt(map.mastery_points);
    // Build columns for the applicable mastery levels
    map.levels.forEach((lev) => {
      const ml = levels.find((level) => level.id === lev.mastery_level_id);
      rowObj[0][lev.id] = { value: parseInt(lev.points) };
      const obj = {
        id: lev.mastery_level_id,
        key: lev.id,
        score: parseInt(lev.points),
        name: (
          <div
            style={{
              textAlign: "center",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            <TruncateToolTip
              renderTip={<div style={{ maxWidth: 400 }}>{ml.level}</div>}
              maxLines={1}
            >
              {ml.level}
            </TruncateToolTip>
          </div>
        ),
        colSpan(args) {
          if (args.type === "HEADER") {
            return calculateColumnSpan(lev, map);
          }
        },
        renderCell: renderContent,
      };
      colsObj.push(obj);
      // Finding the rating for each mastery level for each outcome
      rowObj.forEach((row, index) => {
        if (index === 0) {
          row[lev.id] = { value: parseInt(lev.points) };
        } else {
          const outcome = assessment.assignment_rubric.rating_scales.find(
            (rs) => rs.criteria_id === row.id
          );
          const ratingLevel = outcome.rating_levels.find(
            (rl) => rl.insights_rating_level === lev.id
          );
          row[lev.id] = {
            value: ratingLevel.description,
            selected: false,
            aggregate: false,
          };
        }
      });
    });
    colsObj.sort((a, b) => b.score - a.score);
    colsObj.unshift({
      key: "slo",
      name: (
        <ScreenReaderContent>
          {t("juriedAssessment.sloColumn")}
        </ScreenReaderContent>
      ),
      renderCell: renderSLOContent,
    });
    if (!isExternal) {
      // if results are not applicable, remove student from roster, for now.
      colsObj.push({
        key: "na",
        name: <div style={{ textAlign: "center" }}>{t("global.na")}</div>,
        width: 10,
        renderCell: renderNAContent,
      });
    }
    setCols(colsObj);
    setRows(rowObj);
    buildData(rowObj, studentsEvidenceArr, assessTerm, assessment, data);
  };

  const getEvidenceId = (student, assessment) => {
    return !isExternal && assessment.assessment_type !== "external"
      ? student.ddl_evidence_id // Canvas JA assessment
      : isExternal && assessment.assessment_type === "external"
        ? location.state.assessment_id + "/" + student.canvas_id // standard external assessment
        : location.state.assessment_id + "/" + student.id; // external JA assessment
  };

  const buildData = (
    rowObj,
    studentsEvidenceArr,
    assessTerm,
    assessment,
    data
  ) => {
    let initialData = {};
    if (Object.keys(raterData).length > 0) {
      initialData = raterData;
    } else {
      let possiblePoints = 0;
      Object.values(rowObj[0]).forEach((val) => {
        if (val.value !== undefined && val.value > possiblePoints) {
          possiblePoints = val.value;
        }
      });
      studentsEvidenceArr.forEach((student) => {
        const studentData = {};
        rowObj.forEach((r) => {
          if (r.id !== "raterRow") {
            studentData[r.id] = {
              mastery: false,
              score: null,
              possible: possiblePoints,
              percent: null,
              submitted_or_assessed_at: "",
              canvas_user_id:
                isExternal ||
                (isJA && assessment.assessment_type === "external")
                  ? student.canvas_id
                  : student.id,
              canvas_learning_outcome_id: "" + r.canvas_id,
              outcome_id: r.id,
              evidence_id: getEvidenceId(student, assessment),
              purl_assignment_id: location.state.assignment_id,
              assessor_id: user,
              canvas_enrollment_term_id: assessTerm.canvas_id,
              aggregate_score: null,
              ddl_section_id: isExternal
                ? data.section.ddl_id
                : (data.section?.ddl_id ?? ""),
            };
          }
        });
        initialData[isExternal ? student.canvas_id : student.id] = studentData;
      });
    }
    ExternalService.getExternalScoresByAssessor(
      location.state.assignment_id,
      !isExternal ? user : null
    ).then((scores) => {
      if (scores.length > 0) {
        const ungraded_students = [];
        if (!isExternal) {
          setAssessorComplete(true);
          getAssessorsCompleted(assessment);
          if (scores[0].student_id === -1) {
            scores.forEach((score) => (score.student_id = score.evidence_id));
          }
        }
        if (
          !isJA &&
          scores.length < studentsEvidenceArr.length * (rowObj.length - 1)
        ) {
          setIsGradingComplete(false);
        } else if (
          isExternal &&
          scores.length >= studentsEvidenceArr.length * (rowObj.length - 1)
        ) {
          const students_with_scores = scores.map((score) => score.student_id);
          for (const student of studentsEvidenceArr) {
            if (!students_with_scores.includes(student.canvas_id)) {
              ungraded_students.push(student.canvas_id);
            }
          }
          if (ungraded_students.length === 0) {
            setIsGradingComplete(true);
          }
        }
        let total = 0;
        Object.keys(initialData).forEach((student) => {
          Object.keys(initialData[student]).forEach((slo) => {
            const scoreObj = scores.find(
              (score) => student == score.student_id && slo === score.outcome_id
            );
            if (scoreObj) {
              total += scoreObj.score;
              initialData[student][slo].score = scoreObj.score;
              initialData[student][slo].score_finalized = true;
            } else {
              if (isJA) {
                initialData[student][slo].score = "n/a";
              }
            }
          });
        });
        ungraded_students.forEach((student) => {
          if (student && !Object.hasOwn(initialData, student.canvas_id)) {
            initialData[student.canvas_id] = {};
          }
        });
        setScoreAverage(Math.round((total / scores.length) * 100) / 100);
        ExternalService.getAvgJaScores(location.state.assignment_id).then(
          (avgScores) => {
            if (avgScores.length > 0 && avgScores[0].student_id === -1) {
              avgScores.forEach(
                (score) => (score.student_id = score.evidence_id)
              );
            }
            avgScores.forEach((avgScoreObj) => {
              if (initialData[avgScoreObj.student_id]) {
                initialData[avgScoreObj.student_id][
                  avgScoreObj.outcome_id
                ].aggregate_score = avgScoreObj.score;
              }
            });
            setLoading(false);
            setOpenConfirmModal(false);
            setRaterData(initialData);
            getStudentData(
              initialData,
              !isExternal
                ? studentsEvidenceArr[0].id
                : studentsEvidenceArr[0].canvas_id,
              rowObj
            );
          }
        );
      } else {
        setLoading(false);
        setOpenConfirmModal(false);
        setRaterData(initialData);
        getStudentData(
          initialData,
          !isExternal
            ? studentsEvidenceArr[0].id
            : studentsEvidenceArr[0].canvas_id,
          rowObj
        );
      }
    });
  };

  const getStudentData = (data, studentId, rowData) => {
    Object.keys(data[studentId]).forEach((slo) => {
      const score = data[studentId][slo].score;
      const aggregate = data[studentId][slo].aggregate_score;
      rowData.forEach((row) => {
        if (row.id === slo) {
          const grading_complete = Object.hasOwn(
            data[studentId][slo],
            "score_finalized"
          )
            ? data[studentId][slo].score_finalized
            : false;
          row.grading_complete = grading_complete;
          if (score === null || score === "n/a") {
            Object.keys(row).forEach((key) => {
              if (Object.hasOwn(row, key)) {
                if (row[key].selected !== undefined) {
                  row[key].selected = false;
                }
              }
            });
            if (score === "n/a") {
              row.na = true;
            } else {
              row.na = false;
            }
          } else {
            Object.entries(rowData[0]).forEach(([key, val]) => {
              if (typeof val === "object") {
                if (val.value === score) {
                  row[key].selected = true;
                  row.na = false;
                } else {
                  row[key].selected = false;
                }
              }
            });
          }
          let foundAggregate = false;
          let temp = {};
          Object.entries(rowData[0]).forEach(([key, val]) => {
            if (typeof val === "object") {
              if (val.value === aggregate) {
                row[key].aggregate = true;
                row[key].avgScore = aggregate;
                foundAggregate = true;
              } else {
                row[key].aggregate = false;
                if (Object.keys(temp).length === 0) {
                  temp = {
                    rowId: key,
                    diff: Math.abs(val.value - aggregate),
                    score: val.value,
                  };
                } else if (
                  Math.abs(val.value - aggregate) < temp.diff ||
                  (Math.abs(val.value - aggregate) === temp.diff &&
                    val.value > temp.score)
                ) {
                  temp.rowId = key;
                  temp.diff = Math.abs(val.value - aggregate);
                  temp.score = val.value;
                }
              }
            }
          });
          if (!foundAggregate) {
            row[temp.rowId].aggregate = true;
            row[temp.rowId].avgScore = aggregate;
          }
        }
      });
    });
    setRows(JSON.parse(JSON.stringify(rowData)));
  };

  useEffect(() => {
    const updatedRows = [...rows];
    if (scoreView === "myScore") {
      updatedRows.forEach((row) => {
        row.hideSelected = false;
        row.hideAggregate = true;
      });
    } else if (scoreView === "aggregate") {
      updatedRows.forEach((row) => {
        row.hideSelected = true;
        row.hideAggregate = false;
      });
    } else if (scoreView === "compare") {
      updatedRows.forEach((row) => {
        row.hideSelected = false;
        row.hideAggregate = false;
      });
    }
    setRows(JSON.parse(JSON.stringify(updatedRows)));
  }, [scoreView, setRows]);

  // these all should hypothetically be lists of strings? but I'd guess if 1 is
  const getAssessorsCompleted = (assessment) => {
    ExternalService.getAssignmentById(location.state.assignment_id).then(
      (data) => {
        setAssessorsTotal(assessment.assessors.split(",").length);
        setAssessorsComplete(
          data[0].assessors_complete ? data[0].assessors_complete.length : 0
        );
      }
    );
  };

  useEffect(() => {
    if (
      !assessorComplete &&
      !isGradingComplete &&
      Object.keys(raterData).length > 0 &&
      studentArr.length > 0 &&
      studentArr.length === Object.keys(raterData).length
    ) {
      let numAssessed = 0;
      Object.keys(raterData).forEach((student) => {
        const assessed = Object.keys(raterData[student]).every(
          (slo) => raterData[student][slo].score !== null
        );
        if (assessed) {
          numAssessed += 1;
          if (!isExternal) {
            studentArr.find((s) => s.id === student).renderBeforeLabel = (
              <IconCheckDarkSolid
                color="success"
                title={t("rater.gradingComplete")}
              />
            );
          } else {
            studentArr.find(
              (s) => s.canvas_id.toString() === student
            ).renderBeforeLabel = (
              <IconCheckDarkSolid
                color="success"
                title={t("rater.gradingComplete")}
              />
            );
          }
        } else {
          if (!isExternal) {
            studentArr.find((s) => s.id === student).renderBeforeLabel = (
              <IconHourGlassLine
                color="warning"
                title={t("assignment.gradingInProgress")}
              />
            );
          } else {
            studentArr.find(
              (s) => s.canvas_id.toString() === student
            ).renderBeforeLabel = (
              <IconHourGlassLine
                color="warning"
                title={t("assignment.gradingInProgress")}
              />
            );
          }
        }
      });
      setStudentsAssessed(numAssessed);
    }
  }, [assessorComplete, raterData, studentArr, isGradingComplete]);

  const renderContent = ({ row, column }) => {
    let maxTextLines = 9;
    const showAggregate = row[column.key].aggregate && !row.hideAggregate;
    const showSelected = row[column.key].selected && !row.hideSelected;
    if (showAggregate || showSelected) {
      maxTextLines = 6;
    }

    return (
      <div
        style={{
          lineHeight: 1,
          padding: "10px 2px",
          display: "block",
          position: "relative",
          textWrap: "wrap",
          height: "150px",
          textAlign: row.id === "raterRow" ? "center" : "left",
          fontWeight: row.id === "raterRow" && "bold",
        }}
      >
        <TruncateToolTip
          renderTip={
            <div style={{ maxWidth: 400 }}>{row[column.key].value}</div>
          }
          maxLines={maxTextLines}
        >
          {row[column.key].value.toString()}
        </TruncateToolTip>
        <div
          style={{
            position: "absolute",
            justifyContent: "flex-end",
            left: 5,
            bottom: 10,
          }}
        >
          {showAggregate && (
            <Tooltip
              renderTip={`${t("juriedAssessment.aggregateScore")} (${
                row[column.key].avgScore
              })`}
            >
              <IconGroupSolid
                size="small"
                color="brand"
                style={{ marginRight: "10px" }}
              />
            </Tooltip>
          )}
          {showSelected && (
            <Tooltip renderTip={t("juriedAssessment.myScore")}>
              <IconUserSolid size="small" color="success" />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const renderSLOContent = ({ row, column }) => {
    return (
      <div
        style={{
          lineHeight: 1,
          padding: "10px 2px",
          display: "block",
          position: "relative",
          textWrap: "wrap",
          height: "100%",
        }}
      >
        <TruncateToolTip
          renderTip={<div style={{ maxWidth: 400 }}>{row[column.key]}</div>}
          maxLines={9}
        >
          {row[column.key]}
        </TruncateToolTip>
      </div>
    );
  };

  const renderNAContent = ({ row, column }) => {
    const showSelected = !row.hideSelected;

    return (
      <div style={{ textAlign: "center" }}>
        {row[column.key] && showSelected && (
          <IconCheckMarkSolid size="small" color="success" />
        )}
      </div>
    );
  };

  const handleSubmitEvidenceFiles = async (evidenceFiles) => {
    await ExternalService.saveFiles(
      evidenceFiles,
      "evidence",
      location.state.assessment_id + "/" + selectedStudent.canvas_id.toString(),
      "evidence.zip"
    )
      .then(async () => {
        setEvidenceModalOpen(false);
        showNotification(t("juriedAssessment.uploadEvidenceSuccess"));
        await sleep(3000);
        setFetchEvidence(true);
      })
      .catch(() => {
        showNotification(t("notifications.general.uploadError"), ERROR);
      });
  };

  const handleDownloadEvidence = () => {
    if (selectedStudent) {
      if (!isJA || (assessment.assessment_type === "external" && isJA)) {
        setDownloading(true);
        const id = isJA ? selectedStudent.id : selectedStudent.canvas_id;
        ExternalService.downloadFiles(
          "evidence",
          location.state.assessment_id + "/" + id
        )
          .catch(() => {
            showNotification(
              t("notifications.general.fetchError", {
                obj: t("evidence.evidence"),
              }),
              ERROR
            );
          })
          .finally(() => {
            setDownloading(false);
          });
      } else {
        if (selectedStudent && selectedStudent.attachments) {
          selectedStudent.attachments.forEach((attachment) => {
            downloadFile(attachment.url, attachment.name);
          });
        }
      }
    }
  };

  const handleStudentSelect = (studentId) => {
    const student = studentArr.find((student) => studentId === student.id);
    setSelectedStudent(student);
    getStudentData(
      raterData,
      !isExternal ? student.id : student.canvas_id,
      rows
    );
  };

  const handlePreviousStudent = () => {
    const currIndex = studentArr.findIndex((x) => selectedStudent.id === x.id);
    if (currIndex - 1 >= 0) {
      setSelectedStudent(studentArr[currIndex - 1]);
      getStudentData(
        raterData,
        !isExternal
          ? studentArr[currIndex - 1].id
          : studentArr[currIndex - 1].canvas_id,
        rows
      );
    }
  };

  const handleNextStudent = () => {
    const currIndex = studentArr.findIndex((x) => selectedStudent.id === x.id);
    if (currIndex + 1 < studentArr.length) {
      setSelectedStudent(studentArr[currIndex + 1]);
      getStudentData(
        raterData,
        !isExternal
          ? studentArr[currIndex + 1].id
          : studentArr[currIndex + 1].canvas_id,
        rows
      );
    }
  };

  const handleSave = () => {
    try {
      window.localStorage.setItem(
        `raterData/${location.state.share_id}`,
        JSON.stringify(raterData)
      );
      showNotification(t("notifications.ja.saveSuccess"));
    } catch {
      showNotification(t("notifications.ja.saveError"), ERROR);
    }
  };

  const handleRaterSubmit = () => {
    setSubmittingScores(true);
    let submission = [];
    const submission_time = new Date().toISOString();
    Object.keys(raterData).forEach((student) => {
      Object.values(raterData[student]).forEach((val) => {
        if (
          (!isExternal && val.score !== "n/a") ||
          (isExternal && val.score !== null)
        ) {
          val.percent = Math.round((val.score / val.possible) * 100) / 100;
          val.submitted_or_assessed_at = submission_time;
          val.mastery = val.score >= masteryAt;
          submission.push(val);
        }
      });
    });
    submission = submission.filter(
      (grade) =>
        !Object.hasOwn(grade, "score_finalized") || !grade.score_finalized
    );
    ExternalService.submitPurlResults({
      purlResults: submission,
      purlResultsType: !isExternal
        ? "juried_assessment"
        : "external_assessment",
    })
      .then(() => {
        window.localStorage.removeItem(`raterData/${location.state.share_id}`);
        Object.keys(raterData).forEach((student) => {
          Object.values(raterData[student]).forEach((val) => {
            if (!isExternal || (isExternal && val.score !== null)) {
              val.score_finalized = true;
            }
          });
        });
        setRaterData(raterData);
        showNotification(t("rater.submitSuccess"));
      })
      .catch(() => {
        showNotification(t("rater.submitError"), ERROR);
      })
      .finally(() => setSubmittingScores(false));
  };

  const handleDownloadGuide = () => {
    downloadFile(guide["file_url"], guide["file_name"]);
  };

  return (
    <>
      {!openConfirmModal && (isExternal ? section : true) && !loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: auth ? "calc(100vh - 3rem)" : "calc(100vh - 3rem - 3.5rem)",
          }}
        >
          <div
            style={{
              borderBottom: "0.0625rem solid #C7CDD1",
              margin: "0 0 0.75rem 0",
            }}
          >
            <Flex justifyItems="space-between" wrap="wrap">
              <Flex.Item>
                <Heading as="h1" level="h2">
                  {isJA
                    ? t("assessment.juriedAssessment")
                    : t("assessment.externalAssessment")}
                  {" - "}
                  {assessingTerm.name}
                </Heading>
              </Flex.Item>
              <Flex.Item>
                <Text>{`${t("dashboard.welcomeText")}, ${user}`}</Text>
              </Flex.Item>
            </Flex>
          </div>
          <Flex margin="0 0 medium 0" gap="small">
            <Flex.Item shouldGrow>
              <Text>{section ? section.name : ""}</Text>
            </Flex.Item>
            {isExternal && (
              <Flex.Item>
                <CondensedButton
                  size="small"
                  renderIcon={IconGradebookLine}
                  onClick={() => setUploadDialog(true)}
                >
                  {t("rater.manageStudentRoster")}
                </CondensedButton>
              </Flex.Item>
            )}
            {guide && (
              <Flex.Item>
                <CondensedButton
                  size="small"
                  renderIcon={IconDownloadLine}
                  onClick={handleDownloadGuide}
                >
                  {t("guide.downloadAssessmentGuide")}
                </CondensedButton>
              </Flex.Item>
            )}
          </Flex>
          <Flex gap="small" margin="0 0 small 0" wrap="wrap" justifyItems="end">
            {((!isExternal && !assessorComplete) ||
              (isExternal && !isGradingComplete)) &&
              studentArr.length > 0 && (
                <Flex.Item align="end" margin="0 small 0 0">
                  <ProgressBar
                    size="x-small"
                    screenReaderLabel={t("juriedAssessment.loadingCompletion")}
                    meterColor={({ valueNow, valueMax }) => {
                      if (valueNow / valueMax < 0.5) {
                        return "danger";
                      } else if (valueNow / valueMax >= 1) {
                        return "success";
                      } else {
                        return "info";
                      }
                    }}
                    valueNow={studentsAssessed}
                    valueMax={studentArr.length}
                  />
                  <Text size="medium" weight="bold">
                    {`${studentsAssessed} ${t("juriedAssessment.of")} ${
                      studentArr.length
                    } ${t("juriedAssessment.studentsAssessed")}`}
                  </Text>
                </Flex.Item>
              )}
            <Flex.Item>
              <Flex justifyItems="end" alignItems="end" gap="small">
                <Flex.Item>
                  <IconButton
                    size="small"
                    screenReaderLabel={t("juriedAssessment.previousStudent")}
                    onClick={handlePreviousStudent}
                    disabled={
                      studentArr.length === 0 ||
                      selectedStudent?.id === studentArr[0]?.id
                    }
                  >
                    <IconArrowOpenStartLine />
                  </IconButton>
                </Flex.Item>
                <Flex.Item>
                  <Selector
                    type={SINGLE}
                    id="student-selector"
                    label={t("juriedAssessment.selectStudent")}
                    options={studentArr}
                    selectHandler={handleStudentSelect}
                    selectedItemId={selectedStudent && selectedStudent.id}
                    size="small"
                    placeholder={
                      studentArr.length === 0
                        ? t("juriedAssessment.noStudentSelected")
                        : ""
                    }
                    interaction={studentArr.length > 0}
                  />
                </Flex.Item>
                <Flex.Item>
                  <IconButton
                    size="small"
                    screenReaderLabel={t("juriedAssessment.nextStudent")}
                    onClick={handleNextStudent}
                    disabled={
                      studentArr.length === 0 ||
                      selectedStudent?.id ===
                        studentArr[studentArr.length - 1]?.id
                    }
                  >
                    <IconArrowOpenEndLine />
                  </IconButton>
                </Flex.Item>
              </Flex>
            </Flex.Item>
            {studentArr.length > 0 && (
              <Flex.Item width="100%">
                <Flex gap="small" wrap="wrap">
                  <Flex.Item shouldGrow>
                    <Checkbox
                      label={t("documentViewer.hideEvidenceViewer")}
                      variant="toggle"
                      checked={hideEvidenceViewer}
                      onChange={(e) => setHideEvidenceViewer(e.target.checked)}
                    />
                  </Flex.Item>
                  {isExternal && (
                    <Flex.Item>
                      <CondensedButton
                        size="small"
                        renderIcon={IconUploadLine}
                        onClick={() => {
                          setEvidenceModalOpen(true);
                        }}
                      >
                        {t("juriedAssessment.uploadEvidence")}
                      </CondensedButton>
                    </Flex.Item>
                  )}
                  {isExternal && (
                    <Flex.Item>
                      <CondensedButton
                        size="small"
                        renderIcon={
                          downloading ? (
                            <Spinner
                              renderTitle={t("global.pleaseWait")}
                              size="x-small"
                            />
                          ) : (
                            IconDownloadLine
                          )
                        }
                        onClick={handleDownloadEvidence}
                        disabled={downloading}
                      >
                        {t("juriedAssessment.downloadStudentEvidence")}
                      </CondensedButton>
                    </Flex.Item>
                  )}
                </Flex>
              </Flex.Item>
            )}
          </Flex>
          <div
            style={{
              display: "flex",
              flexGrow: 1,
              flexShrink: 1,
              overflowY: "hidden",
              overflowX: "hidden",
            }}
          >
            {!hideEvidenceViewer && studentArr.length > 0 && (
              <div
                style={{
                  width: "50%",
                  paddingRight: "1.5rem",
                  paddingTop: "0.375rem",
                  borderRight: "0.0625rem solid #C7CDD1",
                }}
              >
                <DocumentViewer
                  selectedStudent={selectedStudent}
                  isJA={isJA}
                  assessmentId={location.state.assessment_id}
                  fetchEvidence={fetchEvidence}
                  setFetchEvidence={setFetchEvidence}
                />
              </div>
            )}
            <div
              style={{
                width:
                  hideEvidenceViewer || studentArr.length === 0
                    ? "100%"
                    : "50%",
                overflowY: "auto",
                overflowX: "hidden",
                paddingLeft:
                  hideEvidenceViewer || studentArr.length === 0
                    ? "0.375rem"
                    : "1.5rem",
              }}
            >
              {studentArr.length !== 0 ? (
                <>
                  <RaterGrid
                    rows={rows}
                    setRows={setRows}
                    cols={cols}
                    raterData={raterData}
                    setRaterData={setRaterData}
                    selectedStudent={selectedStudent}
                    assessorComplete={assessorComplete}
                    isGradingComplete={isGradingComplete}
                    isExternal={isExternal}
                    hideEvidenceViewer={hideEvidenceViewer}
                  />
                  <Flex
                    justifyItems={assessorComplete ? "space-between" : "end"}
                    alignItems="start"
                    margin="small 0"
                    gap="small"
                    wrap="wrap"
                  >
                    {assessorComplete && !isExternal && (
                      <Flex.Item>
                        <RadioInputGroup
                          size="small"
                          layout="columns"
                          name="scoreFilter"
                          defaultValue="myScore"
                          description={
                            <ScreenReaderContent>
                              {t("juriedAssessment.filterBy")}
                            </ScreenReaderContent>
                          }
                          onChange={(e) => setScoreView(e.target.value)}
                        >
                          {inputs.map((input) => (
                            <RadioInput
                              key={input.value}
                              value={input.value}
                              label={input.label}
                            />
                          ))}
                        </RadioInputGroup>
                      </Flex.Item>
                    )}
                    {assessorComplete && !isExternal && (
                      <Flex.Item shouldGrow>
                        <Text>{`*${assessorsComplete} / ${assessorsTotal} ${t(
                          "juriedAssessment.assessorsCompleted"
                        )}`}</Text>
                      </Flex.Item>
                    )}
                    {isGradingComplete && isExternal && scoreAverage && (
                      <Flex.Item shouldGrow>
                        <Text>{`${t("rater.gradingComplete")} (${t(
                          "rater.scoreAvg"
                        )}:  ${scoreAverage}) `}</Text>
                      </Flex.Item>
                    )}
                    <Flex.Item>
                      <Flex justifyItems="end" gap="small" wrap="wrap">
                        <Flex.Item>
                          <Button
                            size="small"
                            onClick={handlePreviousStudent}
                            disabled={
                              !selectedStudent ||
                              (selectedStudent.id &&
                                selectedStudent.id === studentArr[0].id)
                            }
                          >
                            {t("juriedAssessment.previousStudent")}
                          </Button>
                        </Flex.Item>
                        <Flex.Item>
                          <Button
                            size="small"
                            onClick={handleNextStudent}
                            disabled={
                              !selectedStudent ||
                              (selectedStudent.id &&
                                selectedStudent.id ===
                                  studentArr[studentArr.length - 1].id)
                            }
                          >
                            {t("juriedAssessment.nextStudent")}
                          </Button>
                        </Flex.Item>
                        {!assessorComplete && !isGradingComplete && (
                          <Flex.Item>
                            <Button size="small" onClick={handleSave}>
                              {t("global.save")}
                            </Button>
                          </Flex.Item>
                        )}
                        {!assessorComplete && !isGradingComplete && (
                          <Flex.Item>
                            <Button
                              size="small"
                              color="primary"
                              disabled={
                                (studentsAssessed !== studentArr.length &&
                                  !isExternal) ||
                                submittingScores
                              }
                              onClick={handleRaterSubmit}
                            >
                              {submittingScores ? (
                                <Spinner
                                  renderTitle={t("global.pleaseWait")}
                                  size="x-small"
                                />
                              ) : (
                                t("juriedAssessment.finalizeSubmit")
                              )}
                            </Button>
                          </Flex.Item>
                        )}
                      </Flex>
                    </Flex.Item>
                  </Flex>
                  {(assessorComplete || isExternal) && (
                    <Checkbox
                      label={
                        !isExternal
                          ? t("juriedAssessment.toggleFindingsRecs")
                          : t("rater.toggleActionsReflections")
                      }
                      variant="toggle"
                      onChange={() => setToggleFindings(!toggleFindings)}
                      checked={toggleFindings}
                    />
                  )}
                  {assessorComplete &&
                    toggleFindings &&
                    location.state.assessment_id && (
                      <Flex>
                        <Flex.Item size="100%" margin="small 0">
                          <View as="div">
                            <Findings
                              id={location.state.assessment_id}
                              label={
                                <Heading level="h4">
                                  {t("juriedAssessment.findings")}
                                </Heading>
                              }
                              permission
                            />
                          </View>
                          <View as="div">
                            <Recommendations
                              id={location.state.assessment_id}
                              label={
                                <Heading level="h4">
                                  {t("juriedAssessment.recommendations")}
                                </Heading>
                              }
                              permission
                            />
                          </View>
                        </Flex.Item>
                      </Flex>
                    )}
                  {toggleFindings && isExternal && (
                    <Flex padding="small 0 0 0">
                      <Flex.Item shouldShrink shouldGrow size="48%">
                        <Tabs
                          onRequestTabChange={(_e, { index }) =>
                            setTabIndex(index)
                          }
                        >
                          <Tabs.Panel
                            id="reflections-faculty"
                            renderTitle={t("reflections.reflections")}
                            isSelected={tabIndex === 0}
                            padding="small 0"
                          >
                            <div style={{ padding: "0 10px" }}>
                              <ContextReflections
                                contextId={section.ddl_id}
                                externalUserName={user}
                                contextName={section.name}
                              />
                            </div>
                          </Tabs.Panel>
                          <Tabs.Panel
                            id="actions-faculty"
                            renderTitle={t("actions.actions")}
                            isSelected={tabIndex === 1}
                          >
                            <div style={{ padding: "0 10px" }}>
                              <Actions
                                contextId={section.ddl_id}
                                externalUserName={user}
                              />
                            </div>
                          </Tabs.Panel>
                        </Tabs>
                      </Flex.Item>
                    </Flex>
                  )}
                </>
              ) : !isExternal ? (
                <Alert>{t("juriedAssessment.noEvidence")}</Alert>
              ) : (
                <Alert>{t("rater.noRoster")}</Alert>
              )}
            </div>
          </div>
          {section && (
            <RosterManagementTray
              open={uploadDialog}
              setOpen={setUploadDialog}
              roster={roster}
              setRoster={setRoster}
              fetchData={fetchData}
              contextId={section.ddl_id}
              raterData={raterData}
              setRaterData={setRaterData}
              setIsGradingComplete={setIsGradingComplete}
              user={user}
              locationState={location.state}
            />
          )}
          {section && selectedStudent && (
            <UploadFileModal
              shouldOpen={evidenceModalOpen}
              modalHeaderText={
                t("rater.uploadEvidenceFor") + selectedStudent.label
              }
              fileDropHeader={t("juriedAssessment.fileDrop")}
              handleCancel={() => setEvidenceModalOpen(false)}
              handleSubmit={handleSubmitEvidenceFiles}
              multiFileSupport={true}
              fileExtensions={evidenceFileTypes}
              modalCopy={
                <Text as="div" lineHeight="double">
                  {t("juriedAssessment.uploadInstructions")}
                </Text>
              }
            />
          )}
        </div>
      ) : (
        <RaterSkeleton />
      )}
      <ConfirmedModal
        open={openConfirmModal}
        setUser={setUser}
        shareCode={location.state.share_id}
      />
    </>
  );
};

export default Rater;
